import { ReactNode, useContext, useEffect } from "react"
import { NavLink } from "react-router-dom"
import { classNames } from "primereact/utils"
import styles from "./styles.module.css"
import userContext from "../../context/user"
import { useWindowWidth } from "../../common/useWindowWidth"
import { Address } from "../../api/types/address"
import Logo from "../common/Logo"
import { Divider } from "primereact/divider"

export default function Sidebar({ isOpen, toggle }: Props) {
  const { user, logout } = useContext(userContext)
  const width = useWindowWidth()

  useEffect(() => {
    if (
      (isOpen && width <= 576) ||
      (!isOpen && width > 576)
    ) {
      toggle()
    }
  }, [width, isOpen, toggle])

  const sidebarLinkClassNames = ({ isActive }: { isActive: boolean }) => classNames({
    [styles.sidebarLink]: true,
    [styles.sidebarLinkActive]: isActive,
    [styles.sidebarLinkCollapsed]: !isOpen,
  })

  const userInfoClassNames = "overflow-hidden white-space-nowrap text-overflow-ellipsis"

  const getUserCompany = (): ReactNode => {
    if (user) {
      const address = user.addresses.find((address: Address) => address.isMain)

      if (address) {
        return (
          <>
            <div className={userInfoClassNames}>{address.name}</div>
            <div className={userInfoClassNames}>{address.street}, {address.zip}</div>
            <div className={userInfoClassNames}>{address.city}</div>
          </>
        )
      }
    }

    return ""
  }

  const getDepoInfo = (): ReactNode => {
    if (user && user.depo) {
      return (
        <>
          <a
            className={styles.contactLink}
            href={`mailto:${user.depo.email}`}
            title={user.depo.email}
          >
            {user.depo.name}
          </a>
          {user.depo.phones.map(phone => (
            <a
              key={phone}
              className={styles.contactLink}
              href={`callto:${phone}`}
              title={phone}
            >{phone}</a>
          ))}
        </>
      )
    }
    return ""
  }

  const adminIcon = <i className={`pi pi-lock ${styles.adminIcon}`} title={"Admin sekce"} />

  const scrollableMenuItemsHeight = 114 + ((user?.depo.phones.length || 0) * 16)

  return (
    <div className={`shadow-3 ${styles.sidebar}`} style={{ width: isOpen ? 200 : 50 }}>
      <div className={styles.logoWrapper}>
        <Logo />
        <h1 className={"m-0 text-center"}>tPAL</h1>
      </div>

      <div className={styles.scrollableMenuItems} style={{
        height: isOpen ? `calc(100vh - ${scrollableMenuItemsHeight}px - 160px)` : "100vh"
      }}>
        <NavLink to={"/"} className={sidebarLinkClassNames} title={"Domů"}>
          <i className={`pi pi-home ${styles.sidebarLinkIcon}`} />
          <span className={styles.sidebarLinkText}>Domů</span>
        </NavLink>

        <NavLink to={"/orders"} className={sidebarLinkClassNames} title={"Objednávky"}>
          <i className={`pi pi-inbox ${styles.sidebarLinkIcon}`} />
          <span className={styles.sidebarLinkText}>Objednávky</span>
        </NavLink>

        <NavLink to={"/profile"} className={sidebarLinkClassNames} title={"Profil"}>
          <i className={`pi pi-user ${styles.sidebarLinkIcon}`} />
          <span className={styles.sidebarLinkText}>Profil</span>
        </NavLink>

        {(user?.isAdmin || user?.canManageUsers) && (
          <NavLink to={"/users"} className={sidebarLinkClassNames} title={"Uživatelé"}>
            <i className={`pi pi-users ${styles.sidebarLinkIcon}`} />
            <span className={styles.sidebarLinkText}>Uživatelé {adminIcon}</span>
          </NavLink>
        )}

        {user?.isAdmin && (
          <NavLink to={"/addresses"} className={sidebarLinkClassNames} title={"Pobočky"}>
            <i className={`pi pi-building ${styles.sidebarLinkIcon}`} />
            <span className={styles.sidebarLinkText}>Pobočky {adminIcon}</span>
          </NavLink>
        )}

        <NavLink to={"/order-templates"} className={sidebarLinkClassNames} title={"Šablony"}>
          <i className={`pi pi-file ${styles.sidebarLinkIcon}`} />
          <span className={styles.sidebarLinkText}>Šablony</span>
        </NavLink>

        {user?.isAdmin && (
          <NavLink to={"/settings/import"} className={sidebarLinkClassNames} title={"Nastavení"}>
            <i className={`pi pi-cog ${styles.sidebarLinkIcon}`} />
            <span className={styles.sidebarLinkText}>Nastavení {adminIcon}</span>
          </NavLink>
        )}

        <NavLink to={"/address-book"} className={sidebarLinkClassNames} title={"Adresář"}>
          <i className={`pi pi-book ${styles.sidebarLinkIcon}`} />
          <span className={styles.sidebarLinkText}>Adresář</span>
        </NavLink>

        {user?.isShippingPricePageActive && (
          <NavLink to={"/shipping-price"} className={sidebarLinkClassNames} title={"Přepravné"}>
            <i className={`pi pi-dollar ${styles.sidebarLinkIcon}`} />
            <span className={styles.sidebarLinkText}>Přepravné</span>
          </NavLink>
        )}

        <NavLink to={"/logout"} className={sidebarLinkClassNames} title={"Odhlásit"} onClick={logout}>
          <i className={`pi pi-sign-out ${styles.sidebarLinkIcon}`} />
          <span className={styles.sidebarLinkText}>Odhlásit</span>
        </NavLink>
      </div>

      {isOpen && (
        <div
          className={`text-center text-xs ${styles.userInfo}`}
          style={{ height: scrollableMenuItemsHeight }}
        >
          <div className="m-2">
            {getDepoInfo()}
          </div>

          <Divider className="my-2" />

          <div className="m-2">
            <div className={userInfoClassNames}>{user?.firstname} {user?.lastname}</div>
            {getUserCompany()}
          </div>
        </div>
      )}
    </div>
  )
}

interface Props {
  isOpen: boolean;
  toggle: () => void;
}
